import React, { useRef, useState } from 'react';
import withAsyncImport from '../../../withAsyncImport';
import { Col, Row, Button, Title, Label } from '../../../importCL';
import { IOktaProps, TOktaForm, TOktaInputField, TOktaInputValueSetter, TOktaSelectors } from './Okta.types';
import { LinkWrapper } from '../../LinkWrapper/LinkWrapper';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { InputCheckbox, InputText } from '../../../component-library';
import './Okta.css';
const loadOkta = (): Promise<typeof import('./Okta')> => import('./Okta');
const Okta = withAsyncImport(loadOkta);

const OIE_SELECTORS: TOktaSelectors = {
    username: '.o-form-input-name-identifier > input',
    password: '.o-form-input-name-credentials\\.passcode > input',
    rememberMe: '.o-form-input-name-rememberMe input[name="rememberMe"]',
};
const CLASSIC_SELECTORS: TOktaSelectors = {
    username: '#okta-signin-username',
    password: '#okta-signin-password',
    rememberMe: 'input[name="remember"]',
};

const INPUT_VALUE_SETTER: TOktaInputValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

const OktaDeferredSignIn = (props: IOktaProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const pendingSubmitRef = useRef(false);
    const oktaWrapperRef = useRef<HTMLDivElement>(null);
    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const selectors = props.usingOktaIdentityEngine ? OIE_SELECTORS : CLASSIC_SELECTORS;
    const [oktaForm, setOktaForm] = useState<TOktaForm>({
        username: '',
        password: '',
        rememberMe: false,
    });
    const formRef = useRef<TOktaForm>(oktaForm);
    const updateForm = <FieldName extends keyof TOktaForm>(key: FieldName, value: TOktaForm[FieldName]): void => {
        const next = {
            ...formRef.current,
            [key]: value,
        };
        formRef.current = next;
        setOktaForm(next);
    };
    const queryOkta = <ElementType extends Element>(selector: string): ElementType | undefined => oktaWrapperRef.current?.querySelector<ElementType>(selector);
    const handleFieldFocus = (): void => {
        loadOkta().catch((error: unknown): void => {
            console.error('Unable to preload Okta:', error);
        });
    };
    const syncOktaCheckbox = (checked: boolean): void => {
        const checkbox = queryOkta<HTMLInputElement>(selectors.rememberMe);
        if (checkbox && checkbox.checked !== checked) {
            checkbox.click();
        }
    };
    const syncAllFields = (): void => {
        syncOktaInput('username', formRef.current.username);
        syncOktaInput('password', formRef.current.password);
        syncOktaCheckbox(formRef.current.rememberMe);
    };
    const syncOktaInput = (field: TOktaInputField, value: string): void => {
        if (!isLoading) {
            return;
        }
        const input = queryOkta<HTMLInputElement>(selectors[field]);
        if (!input) {
            return;
        }
        INPUT_VALUE_SETTER?.call(input, value);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const handleOktaReady = (): void => {
        syncAllFields();
        setIsReady(true);
        if (pendingSubmitRef.current) {
            pendingSubmitRef.current = false;
            submitOktaForm();
        }
    };
    const submitOktaForm = (): void => {
        const signInButton = queryOkta<HTMLInputElement>('.Billboard__signInButton');
        signInButton?.click();
    };
    const handleDeferredSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (isSubmitting) {
            return;
        }
        updateForm('username', usernameRef.current?.value ?? formRef.current.username);
        updateForm('password', passwordRef.current?.value ?? formRef.current.password);
        setIsSubmitting(true);
        pendingSubmitRef.current = true;
        setIsLoading(true);
    };
    return (
        <>
            {!isReady && (
                <form className='OktaDeferredSignIn block' onSubmit={handleDeferredSubmit}>
                    <Title className='!pl-2 !mb-3 border-l-green-silver-tree border-l-[.1875rem]' tag='h2' verticalBar={true}>
                        {props.signInTitle ?? ''}
                    </Title>
                    <Label textClass='leading-[1.2rem] !text-black'>
                        Username
                    </Label>
                    <InputText
                        className='leading-6 h-12 rounded-[0.1888rem] border border-solid border-grey-silver'
                        id='okta-deferred-username'
                        ref={usernameRef}
                        type='text'
                        value={oktaForm.username}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            updateForm('username', e.target.value);
                        }}
                        onFocus={handleFieldFocus}
                        useSuccessIcon={false}
                        disabled={isSubmitting}
                    />
                    <Label textClass='mt-3 leading-[1.2rem] !text-black'>
                        Password
                    </Label>
                    <InputText
                        className='leading-6 h-12 rounded-[0.1888rem] border border-solid border-grey-silver'
                        id='okta-deferred-password'
                        ref={passwordRef}
                        type='password'
                        value={oktaForm.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            updateForm('password', e.target.value);
                        }}
                        onFocus={handleFieldFocus}
                        useSuccessIcon={false}
                        disabled={isSubmitting}
                    />
                    <div
                        onFocus={handleFieldFocus}
                    >
                        <InputCheckbox
                            label='Remember my browser so I can sign in faster'
                            value={oktaForm.rememberMe}
                            setValue={(value: boolean): void => {
                                updateForm('rememberMe', value);
                            }}
                            id='OktaDeferredSignIn__checkbox'
                            className='mt-3 !text-black'
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className='mt-5'>
                        <Button
                            size='large'
                            className='Billboard__signInButton'
                            type='submit'
                            icon={!isSubmitting ? 'lock' : undefined}
                        >
                            <React.Fragment>
                                {isSubmitting && <svg className="loader" />}
                                {transformHTMLParserLinks(props.signInCtaLabel || '')}
                            </React.Fragment>
                        </Button>
                        <Row className='mx-0'>
                            <Col xs={6} className='!px-0'>
                                <LinkWrapper
                                    href={props.forgotUserNameLink}
                                    className='Billboard__bottomLink block'>
                                    Forgot Username?
                                </LinkWrapper>
                            </Col>
                            <Col xs={6} className='!px-0'>
                                <LinkWrapper
                                    href={props.forgotPasswordLink}
                                    className='Billboard__bottomLink Billboard__bottomLink--right block'>
                                    Forgot Password?
                                </LinkWrapper>
                            </Col>
                        </Row>
                        <Row className='mx-0' align='center'>
                            <Col xs={12} className='Billboard__bottomLinkWrapper px-0'>
                                <LinkWrapper
                                    href={props.setupAccountLink}
                                    className='Billboard__bottomLink block'>
                                    Set up Online Account Access
                                </LinkWrapper>
                            </Col>
                        </Row>
                    </div>
                </form>
            )}
            {isLoading && (
                <div ref={oktaWrapperRef} className={isReady ? 'OktaDeferredSignIn__okta' : 'OktaDeferredSignIn__okta OktaDeferredSignIn__okta--loading'}>
                    <Okta
                        {...props}
                        getInitialUsername={():string => formRef.current.username}
                        getInitialPassword={():string => formRef.current.password}
                        getInitialRememberMe={():boolean => formRef.current.rememberMe}
                        onReady={handleOktaReady}
                    />
                </div>
            )}
        </>
    );
}

export default OktaDeferredSignIn;